import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { MapPin, Phone, Mail, Clock } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ServiceCoverage() {
  const [zip, setZip] = useState("");
  const [coverage, setCoverage] = useState(null);
  const [checking, setChecking] = useState(false);

  const check = async () => {
    if (zip.length < 3) return toast.error("Enter a valid zip code");
    setChecking(true);
    try {
      const { data } = await axios.post(`${API}/zip-check`, { zip_code: zip });
      setCoverage(data);
    } catch {
      toast.error("Could not check zip");
    } finally { setChecking(false); }
  };

  return (
    <section id="reviews" data-testid="coverage-section" className="py-16 lg:py-24">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-2 gap-10">
        <div className="bg-[#0C2E2B] rounded-2xl p-8 lg:p-10 text-white relative overflow-hidden">
          <div className="absolute inset-0 grain-bg opacity-30 pointer-events-none" />
          <div className="relative">
            <div className="font-tactical text-[11px] uppercase tracking-[0.25em] text-[#FFDF00] mb-2">Service Area</div>
            <h3 className="font-display font-black text-3xl lg:text-4xl leading-tight">Check your dispatch zone.</h3>
            <p className="mt-3 text-white/70 text-sm">
              Enter your zip — we'll tell you if you're in our 1-hour rapid dispatch zone.
            </p>
            <div className="mt-6 flex gap-2">
              <Input
                data-testid="zip-code-checker-input"
                placeholder="e.g. 90210"
                value={zip}
                onChange={(e) => setZip(e.target.value)}
                className="bg-white text-zinc-900 rounded-md"
              />
              <Button data-testid="zip-code-checker-btn" onClick={check} disabled={checking} className="bg-[#FFDF00] hover:bg-[#E5C900] text-[#0C2E2B] rounded-md font-bold">
                {checking ? "Checking..." : "Check"}
              </Button>
            </div>
            {coverage && (
              <div data-testid="zip-coverage-result" className="mt-4 bg-white/10 backdrop-blur border border-white/20 rounded-md p-4">
                <div className="font-bold">You're in the {coverage.zone}!</div>
                <div className="text-sm text-white/70 flex items-center gap-1 mt-1">
                  <Clock className="w-3 h-3" /> Typical arrival: {coverage.eta}
                </div>
              </div>
            )}

            <div className="mt-8 pt-6 border-t border-white/15 space-y-3 text-sm">
              <div className="flex items-center gap-3">
                <Phone className="w-4 h-4 text-[#FFDF00]" />
                <a href="tel:+16129907871" data-testid="footer-phone-link" className="font-bold hover:underline">(612) 990-7871 · 24/7 Dispatch</a>
              </div>
              <div className="flex items-center gap-3">
                <Mail className="w-4 h-4 text-[#FFDF00]" />
                <a href="mailto:crew@harveysplace.co" data-testid="footer-email-link" className="font-bold hover:underline">crew@harveysplace.co</a>
              </div>
              <div className="flex items-center gap-3">
                <MapPin className="w-4 h-4 text-[#FFDF00]" />
                <span className="font-bold">Serving Greater Metro Area · Same-day 7 days/week</span>
              </div>
            </div>
          </div>
        </div>

        <div>
          <div className="font-tactical text-[11px] uppercase tracking-[0.25em] text-[#0C2E2B] mb-2">What Customers Say</div>
          <h3 className="font-display font-extrabold text-3xl lg:text-4xl text-zinc-900 leading-tight mb-6">
            "It's the last time I'll ever haggle over a phone quote."
          </h3>
          <div className="space-y-4">
            {[
              { name: "Marcus D.", body: "Snapped 3 photos of my garage. Quote in 15 seconds. Crew was here at 8am the next day. Gone in 40 minutes.", stars: 5 },
              { name: "Jen R.", body: "Fixed the dryer-vent hole my son punched through the wall. You literally cannot see where the damage was.", stars: 5 },
              { name: "Priya S.", body: "Assembled a Pax closet + two Malm dressers in under 2 hours. Boxes gone, everything anchored. Paid $189 total.", stars: 5 },
            ].map((r, i) => (
              <div key={r.name} data-testid={`review-${i + 1}`} className="bg-white border-2 border-zinc-200 rounded-xl p-5 card-lift">
                <div className="flex text-[#FFDF00]">
                  {Array.from({ length: r.stars }).map((_, j) => <span key={`${r.name}-star-${j}`}>★</span>)}
                </div>
                <p className="mt-2 text-sm text-zinc-700 leading-relaxed">{r.body}</p>
                <div className="mt-2 text-xs font-bold text-zinc-500">— {r.name}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
